import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngneat/elf';
import { FormType, createForm, subformComponentProviders, NGX_SUB_FORM_HANDLE_VALUE_CHANGES_RATE_STRATEGIES } from 'ngx-sub-form';
import { Subject, lastValueFrom } from 'rxjs';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { UnitsGridColumn } from 'src/app/shared/components/units-grid-input/units-grid-input.models';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { Packer, PackerEnvelope, Packers } from 'src/app/wellbore-inputs/models/packers.model';
import { PackerService } from 'src/app/wellbore-inputs/services/packers.service';
import { WellTypeService } from 'src/app/wellbore-inputs/services/well-type-datums.service';
import {
  createPackerEnvelopeColumns,
  createPackerEnvelopeRow,
  ensureTrailingInputRow,
  extractPackerEnvelopeRows,
  populatePackerEnvelopeFormArray
} from './helpers/packer-envelope.helper';

interface PackerForm {
  name: string;
  type: string;
  measuredDepth: number;
  initialSetPressure: number;
  plugDepth: number;
  pickupForce: number;
  hasExpansionJoint: boolean;
  advancedExpansionJoint: any;
  isPressureBarrier: boolean;
  packerEnvelope: PackerEnvelope[];
  sort: number;
}

@Component({
  selector: 'app-packer-data',
  templateUrl: './packer-data.component.html',
  styles: [':host { display: block; height: 100%; min-height: 0; }'],
  providers: subformComponentProviders(PackerDataComponent),
  standalone: false
})
export class PackerDataComponent implements OnInit, OnChanges {
  public control(name: string): UntypedFormControl {
    return this.form.formGroup.get(name) as UntypedFormControl;
  }
  
  @Input()
  public userUnits: UserUnitsModel | null | undefined;

  @Input()
  public packerIndex: number;

  @Input()
  public packersStore: Store;

  @Input()
  errorMessage: string;

  @Input()
  public currentString: any;

  public packerTypes: { label: string, value: string }[];
  public packerData: Packers;
  public packerMinDepth: number;
  public packerDefaultDepth: number;
  public isMechanicalPacker: boolean;
  public packerEnvelope: PackerEnvelope[];
  public forceUnit: string;
  public pressureUnit: string;
  public selectedResultDisplay = signal<boolean>(false);
  public plot = {
    data: []
  };
  public xAxisTitle = '';
  public yAxisTitle = '';
  public plotName = 'packerOperatingEnvelope';
  public downloadPlotName = 'packer_operating_envelope';
  public packerError: string;
  public packerDepth: number | undefined;
  public defaultSealBoreDiameter: number | undefined;

  public readonly envelopeColumns = createPackerEnvelopeColumns();
  public packerEnvelopeRows = new FormArray<FormGroup>([]);
  public createPackerEnvelopeRow = createPackerEnvelopeRow;

  constructor(
    @Inject(PackerService) private _packerService: PackerService,
    @Inject(SignalRService) private _signalRService: SignalRService,
    @Inject(WellTypeService) private _wellTypeService: WellTypeService
  ) {
    this.packerTypes = [
      { label: "Mechanical", value: "Mechanical" },
      { label: "Hydraulic", value: "Hydraulic" },
      { label: "Hydrostatic", value: "Hydrostatic" }
    ];
  }

  private input$ = new Subject<Packer | null | undefined>();
  @Input() set packer(packer: Packer | undefined) {
    this.input$.next(packer);
  }

  private disabled$ = new Subject<boolean>();
  @Input() set disabled(value: boolean | undefined) {
    this.disabled$.next(!!value);
  }

  @Output()
  packerUpdate = new EventEmitter<Packer>();

  ngOnChanges(e: any): void {
    if (e.errorMessage) {
      this.packerError = e.errorMessage?.currentValue || '';
    }
    if (e.packer?.currentValue) {
      const envelope = e.packer.currentValue.packerEnvelope || [];
      const currentRows = extractPackerEnvelopeRows(this.packerEnvelopeRows);
      const isSame = this.packerEnvelopeRows.length > 0 && JSON.stringify(envelope) === JSON.stringify(currentRows);
      if (!isSame) {
        this.packerEnvelope = envelope.length > 0 ? envelope : [{ force: 0, differentialPressure: 0 }];
        populatePackerEnvelopeFormArray(this.packerEnvelopeRows, this.packerEnvelope);
      }
    }
    this.plotEnvelope();

    this.defaultSealBoreDiameter = this.currentString?.stringSections.find((section: any) =>
      section.bottomMeasuredDepth >= this.packerDepth)?.pipe.outsideDiameter;
  }

  private plotEnvelope() {
    if (Array.isArray(this.packerEnvelope) && this.packerEnvelope.length) {

      const plotArray = [...this.packerEnvelope];
      if (plotArray[0].force !== plotArray[plotArray.length - 1].force ||
        plotArray[0].differentialPressure !== plotArray[plotArray.length - 1].differentialPressure) {
        plotArray.push(plotArray[0]);
      }

      this.plot.data = [{
        x: plotArray.map(x => x.differentialPressure),
        y: plotArray.map(x => x.force),
        showlegend: false
      }];
    }
  }

  public form = createForm<Packer, PackerForm>(this, {
    formType: FormType.ROOT,
    disabled$: this.disabled$,
    input$: this.input$,
    output$: this.packerUpdate,
    formControls: {
      name: new FormControl('', { updateOn: 'blur', validators: [Validators.required] }),
      type: new FormControl(null, [Validators.required]),
      measuredDepth: new FormControl(null, { updateOn: 'blur', validators: [Validators.required] }),
      initialSetPressure: new FormControl(null, { updateOn: 'blur', validators: [Validators.required] }),
      plugDepth: new FormControl(null, { updateOn: 'blur', validators: [Validators.required] }),
      pickupForce: new FormControl(null, { updateOn: 'blur', validators: [Validators.required] }),
      hasExpansionJoint: new FormControl(false, [Validators.required]),
      advancedExpansionJoint: new FormControl(null),
      isPressureBarrier: new FormControl(null, [Validators.required]),
      packerEnvelope: new FormControl(null),
      sort: new FormControl(null, [Validators.required])
    },
    fromFormGroup: (formValue: PackerForm | null): Packer | null => {
      if (!formValue) {
        return null;
      }

      const { ...commonValues } = formValue;

      if (formValue.type == 'Hydraulic') {
        this.isMechanicalPacker = false;
      }

      if (formValue.hasExpansionJoint == true && !formValue.advancedExpansionJoint) {
        return null;
      }

      return {
        ...commonValues
      }
    },
    handleEmissionRate: NGX_SUB_FORM_HANDLE_VALUE_CHANGES_RATE_STRATEGIES.debounce(1000)
  })

  ngOnInit(): void {
    this.forceUnit = this.userUnits.force;
    this.pressureUnit = unitsLib[this.userUnits.pressure].symbol;

    this.yAxisTitle = `Axial Force: -Down [Compression] / +Up [Tension] (${this.forceUnit})`;
    this.xAxisTitle = `Differential Pressure: -Down [Above] / +Up [Below] (${this.pressureUnit})`;

    this.validatePackers();

    if (!this.packerData) {
      this.isMechanicalPacker = true;
    }

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));
  }

  signalRfunc(data: { action: string, designId: string }) {
    if (data.action == PeriforOnChangeMessages.REFRESH_STRING_INPUTS || data.action === PeriforOnChangeMessages.REFRESH_TUBULARS_LIST ||
      data.action === PeriforOnChangeMessages.REFRESH_PACKERS
    ) {
      this.validatePackers();
    }
  }

  public tablePlotToggle(event: any) {
    this.plotEnvelope();
    this.selectedResultDisplay.set(event.checked);
  }



  public async validatePackers() {
    const [packerData, wellType] = await Promise.all([
      lastValueFrom(this._packerService.getPackersForTubular()),
      lastValueFrom(this._wellTypeService.getWellType())
    ]);
    this.packerData = packerData;

    const mudlineDepth = wellType.mudlineDepth;
    this.packerMinDepth = +Math.max(this.packerData.hangerMd + 0.1, mudlineDepth).toFixed(2);
    this.packerDefaultDepth = Math.min(this.packerData.shoeMd, this.packerData.topOfCementMd);

    this.form.formGroup.controls.measuredDepth.setValidators([Validators.min(this.packerMinDepth), Validators.max(this.packerDefaultDepth)]);
    this.form.formGroup.controls.measuredDepth.updateValueAndValidity({ emitEvent: false });

    const packerType = this.form.formGroup.controls.type.value;
    const isHydraulic = packerType === 'Hydraulic';
    const hasInitialSetPressure = packerType === 'Hydraulic' || packerType === 'Hydrostatic';

    if (isHydraulic) {
      this.form.formGroup.controls.plugDepth.setValidators([
        Validators.required,
        Validators.min(this.form.formGroup.controls.measuredDepth.value),
        Validators.max(this.packerDefaultDepth)
      ]);
    } else {
      this.form.formGroup.controls.plugDepth.clearValidators();
    }
    this.form.formGroup.controls.plugDepth.updateValueAndValidity({ emitEvent: false });

    if (hasInitialSetPressure) {
      this.form.formGroup.controls.initialSetPressure.setValidators([Validators.required]);
    } else {
      this.form.formGroup.controls.initialSetPressure.clearValidators();
    }
    this.form.formGroup.controls.initialSetPressure.updateValueAndValidity({ emitEvent: false });

    this.packerDepth = this.form.formGroup.controls.measuredDepth.value;

    this.defaultSealBoreDiameter = this.currentString?.stringSections.find((section: any) =>
      section.bottomMeasuredDepth >= this.packerDepth)?.pipe.outsideDiameter;

    if (this.form.formGroup.controls.hasExpansionJoint.value == true &&
        this.form.formGroup.controls.advancedExpansionJoint.value?.isDepthNotAtPacker == true &&
      this.form.formGroup.controls.advancedExpansionJoint.value.measuredDepth >= this.packerDepth) {
      const advancedExpansionJoint = this.form.formGroup.controls.advancedExpansionJoint.value;
      this.form.formGroup.controls.advancedExpansionJoint.setValue({
        ...advancedExpansionJoint,
        measuredDepth: this.form.formGroup.controls.measuredDepth.value - 1
      });
    } else {
      this.form.formGroup.controls.advancedExpansionJoint.setValidators([]);
    }
  }

  public expansionJointChange(event: any) {
    if (!event.checked) {
      this.form.formGroup.controls.advancedExpansionJoint.clearValidators();
      this.form.formGroup.controls.advancedExpansionJoint.updateValueAndValidity({ emitEvent: false });
      this.form.formGroup.controls.advancedExpansionJoint.setValue(null);
    }
  }

  public onPackerEnvelopeRowDeleted(index: number): void {
    if (index >= 0 && index < this.packerEnvelopeRows.length) {
      this.packerEnvelopeRows.removeAt(index);
    }
    this.onPackerEnvelopeGridChange();
  }

  public onPackerEnvelopeGridChange(): void {
    ensureTrailingInputRow(this.packerEnvelopeRows);
    const rawRows = extractPackerEnvelopeRows(this.packerEnvelopeRows);
    this.packerEnvelope = rawRows;
    this.form.formGroup.value['packerEnvelope'] = rawRows;
    this.plotEnvelope();
    this.packerUpdate.emit(this.form.formGroup.value);
  }

  public typeChange() {
    if (this.form.formGroup.controls.plugDepth.value == 0) {
      this.form.formGroup.controls.plugDepth.setValue(this.form.formGroup.controls.measuredDepth.value);
    }

    this.validatePackers();
  }

}

